import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, Subscription, of, switchMap, tap } from 'rxjs';
import { GroupService } from './group.service';
import { SocketService } from './socket.service';

@Injectable({
  providedIn: 'root'
})
export class TagService {
  private apiUrl = 'api/tags';
  private tagsSubject = new BehaviorSubject<any>(null);
  tags$ = this.tagsSubject.asObservable();

  private taggedPatientsSubject = new BehaviorSubject<any>(null);
  taggedPatients$ = this.taggedPatientsSubject.asObservable();

  private reloadSub?: Subscription;
  private groupId?: string;

  constructor(
    private http: HttpClient,
    private groupService: GroupService,
    private socketService: SocketService
  ) {

    this.setupRealTimeUpdates();

    this.groupService.selectedGroup$.pipe(
      switchMap(selectedGroup => {
        if (selectedGroup) {
          this.groupId = selectedGroup.id;
          return this.getTagsByGroup(selectedGroup.id);
        }
        return EMPTY;
      })
    ).subscribe();
  }

  private setupRealTimeUpdates(): void {
    // Listen for a new item being added
    this.socketService.listen('tagsUpdated', 'tag-service').subscribe(() => {
      if (this.reloadSub) {
        this.reloadSub.unsubscribe();
      }
      this.reloadSub = this.getTagsByGroup(this.groupId!).subscribe()
    });

    this.socketService.listen('patientUpdated', 'tag-service').subscribe((updatedPatient) => {
      // Get the current value of taggedPatientsSubject
      const current = this.taggedPatientsSubject.getValue();

      if (!current) return;

      // Map through the current tasks list to update the specific patient's details
      const updated = current.map((item: any) => {
        if (item.id === updatedPatient.id) {
          // Update the diagnosis for the specific patient
          return {
            ...item,
            name: updatedPatient.name,
            surname: updatedPatient.surname,
            diagnosis: updatedPatient.diagnosis,
            patientNumber: updatedPatient.patientNumber,
            requiresValidation: updatedPatient.requiresValidation,
            requiresICDValidation: updatedPatient.requiresICDValidation,
            wasSelfBoarded: updatedPatient.wasSelfBoarded,
            status: updatedPatient.status,
          };
        }
        return item;
      });

      // Update the taggedPatientsSubject with the new list
      this.taggedPatientsSubject.next(updated);
    });
  }

  getTagsByGroup(groupId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get(`${this.apiUrl}/group/${groupId}`, { headers: { Authorization: `Bearer ${token}` } })
      .pipe(
        tap((results: any) => { this.tagsSubject.next(results.data || []); })
      );
  }


  getPatientsByTag(tags: string[]): Observable<any> {
    if (!tags?.length) {
      this.taggedPatientsSubject.next([]);;
      return of({ data: [] });
    }
    const token = localStorage.getItem('token');
    const tagParams = tags.map(tag => `tags=${encodeURIComponent(tag)}`).join('&');
    const url = `${this.apiUrl}/patients/${this.groupId}?${tagParams}`;

    return this.http.get(url, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).pipe(
      tap(results => {
        this.taggedPatientsSubject.next(results);
      })
    );
  }

}
